import React, { useEffect } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { groupBy, flatten, reduce, max } from "lodash"
import {
  Drawer,
  makeStyles,
  Divider,
  Hidden,
  Typography,
  Box,
  Dialog,
  IconButton,
  useMediaQuery,
  Theme,
  Button,
} from "@material-ui/core"
import { GetAllTags } from "../__generated__/GetAllTags"
import ArrowBackIcon from "@material-ui/icons/ArrowBack"
import { gql } from "@apollo/client"
import { Tag } from "./Tag"
import { MultiSelectTagGroup } from "./MultiSelectTagGroup"
import {
  SearchQueryParameter,
  useSearchParameters,
} from "../../useSearchParameters"
import { ValueRangeTagGroup } from "./ValueRangeTagGroup"
import { SingleSelectTag } from "./SingleSelectTag"

interface QueryHistogramResult {
  type: Tag["type"]
  value: string
  value_count: number
}

export type HidableFilters = "style"

interface TagFilterProps {
  queryHistogramResult?: QueryHistogramResult[]
  initialSelected?: Set<string>
  mobileTagFilterOpen: boolean
  handleCloseMobileTagFilter: () => void
  hiddenFilters?: HidableFilters[]
}

export const getTagId = (tag: Omit<Tag, "id">) => `${tag.type}.${tag.value}`

type RangeType = [number | undefined, number | undefined]

export const TagFilter: React.FC<TagFilterProps> = ({
  queryHistogramResult,
  initialSelected,
  mobileTagFilterOpen,
  hiddenFilters,
  handleCloseMobileTagFilter,
}) => {
  const {
    hasura: { query_histogram },
  }: GetAllTags = useStaticQuery(
    graphql`
      query GetAllTags {
        hasura {
          query_histogram(args: { category: "whisky" }) {
            type
            value
            value_count
          }
        }
      }
    `
  )

  const [query, setQuery] = useSearchParameters()
  const [selected, setSelected] = React.useState<Set<string>>(
    initialSelected || new Set()
  )
  const [abvRange, setAbvRange] = React.useState<RangeType>([
    undefined,
    undefined,
  ])
  const [priceRange, setPriceRange] = React.useState<RangeType>([
    undefined,
    undefined,
  ])
  const [volumeRange, setVolumeRange] = React.useState<RangeType>([
    undefined,
    undefined,
  ])

  const [sale, setSale] = React.useState<boolean>(false)

  useEffect(() => {
    const filters = flatten([
      query.style?.map(value => ({ type: "style", value })),
      query.region?.map(value => ({ type: "region", value })),
      query.country?.map(value => ({ type: "country", value })),
      query.distillery?.map(value => ({ type: "distillery", value })),
    ]).map(getTagId)

    if (query.abvmin !== abvRange[0] || query.abvmax !== abvRange[1]) {
      setAbvRange([query.abvmin, query.abvmax])
    }

    if (query.costmin !== priceRange[0] || query.costmax !== priceRange[1]) {
      setPriceRange([query.costmin, query.costmax])
    }

    if (
      query.volumemin !== volumeRange[0] ||
      query.volumemax !== volumeRange[1]
    ) {
      setVolumeRange([query.volumemin, query.volumemax])
    }

    setSale(!!query.isonsale)

    setSelected(new Set(filters))
  }, [query])

  const tagCounts = React.useMemo(
    () =>
      reduce(
        queryHistogramResult,
        (counts: Record<string, number>, currCount) => {
          counts[getTagId(currCount)] = currCount.value_count
          return counts
        },
        {}
      ),
    [queryHistogramResult]
  )

  const [tagGroups, initialTagCounts] = React.useMemo(() => {
    const tags = query_histogram.map(tag => ({
      ...tag,
      count: tag.value_count,
      id: getTagId(tag as any),
    })) as Tag[]
    return [
      groupBy(tags, "type"),
      tags.reduce((record, tag) => {
        record[tag.id] = tag.count || 0
        return record
      }, {} as Record<string, number>),
    ]
  }, query_histogram)

  const volumeMax = React.useMemo(() => {
    return max(
      tagGroups["volume"].map(tag => Number.parseFloat(tag.value))
    ) as number
  }, [tagGroups])

  const onFilterSelected = (
    type: SearchQueryParameter,
    value: string | number | undefined
  ) => {
    setQuery(type, value)
  }

  const onIsOnSaleChecked = () => setQuery("isonsale", sale ? undefined : !sale)

  return (
    <>
      <Hidden mdDown>
        <DesktopFilterContainer
          selected={selected}
          tagCounts={tagCounts || initialTagCounts}
          tagGroups={tagGroups}
          abvRange={abvRange}
          priceRange={priceRange}
          volumeRange={volumeRange}
          volumeMax={volumeMax}
          onFilterSelected={onFilterSelected}
          sale={sale}
          onIsOnSaleChecked={onIsOnSaleChecked}
          hiddenFilters={hiddenFilters}
        />
      </Hidden>
      <Hidden lgUp>
        <MobileFilterContainer
          selected={selected}
          tagCounts={tagCounts || initialTagCounts}
          tagGroups={tagGroups}
          abvRange={abvRange}
          priceRange={priceRange}
          volumeRange={volumeRange}
          volumeMax={volumeMax}
          onFilterSelected={onFilterSelected}
          sale={sale}
          onIsOnSaleChecked={onIsOnSaleChecked}
          mobileTagFilterOpen={mobileTagFilterOpen}
          handleCloseMobileTagFilter={handleCloseMobileTagFilter}
          hiddenFilters={hiddenFilters}
        />
      </Hidden>
    </>
  )
}

interface FilterContentProps {
  priceRange: RangeType
  volumeRange: RangeType
  abvRange: RangeType
  volumeMax: number
  tagCounts: Record<string, number>
  tagGroups: Record<string, Tag[]>
  selected: Set<string>
  sale: boolean
  onIsOnSaleChecked: () => void
  onFilterSelected: (
    type: SearchQueryParameter,
    value: string | number | boolean | undefined
  ) => void
  hiddenFilters?: HidableFilters[]
}

const FilterContent: React.FC<FilterContentProps> = ({
  tagCounts,
  tagGroups,
  onFilterSelected,
  sale,
  priceRange,
  abvRange,
  volumeRange,
  selected,
  volumeMax,
  onIsOnSaleChecked,
  hiddenFilters,
}) => {
  const hiddenSet = new Set(hiddenFilters)
  return (
    <>
      <Box ml={1} mt={1}>
        <Typography variant="h6" component="h2">
          Listing Filters
        </Typography>
        <Typography variant="caption">
          Filter out products whose listings don't match the filters
        </Typography>
      </Box>
      <Box mt={3}>
        <SingleSelectTag
          isChecked={sale}
          title="Sale"
          option="Only discounted items"
          onChecked={onIsOnSaleChecked}
        ></SingleSelectTag>
      </Box>
      <Box ml={1} mr={2} mt={3}>
        <ValueRangeTagGroup
          name="Price"
          range={[0, 1000]}
          minType="costmin"
          maxType="costmax"
          valueSymbol="£"
          currentSelection={priceRange}
          tagCounts={tagCounts}
          onFilterSelected={onFilterSelected}
        ></ValueRangeTagGroup>
      </Box>
      <Box ml={1} mr={2} mt={3}>
        <ValueRangeTagGroup
          name="Volume"
          range={[0, volumeMax]}
          minType="volumemin"
          maxType="volumemax"
          valueSymbol="cl"
          currentSelection={volumeRange}
          tagCounts={tagCounts}
          onFilterSelected={onFilterSelected}
        ></ValueRangeTagGroup>
      </Box>

      <Box ml={1} mt={4}>
        <Typography variant="h6" component="h2">
          Product Filters
        </Typography>
        <Typography variant="caption">
          Filter out products not matching the filters
        </Typography>
      </Box>
      <Box ml={1} mr={2} mt={3}>
        <ValueRangeTagGroup
          name="ABV"
          minType="abvmin"
          maxType="abvmax"
          valueSymbol="%"
          range={[0, 100]}
          currentSelection={abvRange}
          tagCounts={tagCounts}
          onFilterSelected={onFilterSelected}
        ></ValueRangeTagGroup>
      </Box>
      {!hiddenSet.has("style") && (
        <Box mt={3}>
          <MultiSelectTagGroup
            name="Style"
            tags={tagGroups["style"]}
            selectedTags={selected}
            onTagSelected={onFilterSelected}
            tagCounts={tagCounts}
          ></MultiSelectTagGroup>
        </Box>
      )}
      <Box mt={3}>
        <MultiSelectTagGroup
          name="Country"
          tags={tagGroups["country"]}
          selectedTags={selected}
          onTagSelected={onFilterSelected}
          tagCounts={tagCounts}
        ></MultiSelectTagGroup>
      </Box>
      <Box mt={3}>
        <MultiSelectTagGroup
          name="Region"
          tags={tagGroups["region"]}
          selectedTags={selected}
          onTagSelected={onFilterSelected}
          tagCounts={tagCounts}
        ></MultiSelectTagGroup>
      </Box>
      <Box mt={3}>
        <MultiSelectTagGroup
          name="Distillery"
          tags={tagGroups["distillery"]}
          selectedTags={selected}
          onTagSelected={onFilterSelected}
          tagCounts={tagCounts}
        ></MultiSelectTagGroup>
      </Box>
    </>
  )
}

interface MobileFilterContainerProps extends FilterContentProps {
  mobileTagFilterOpen: boolean
  handleCloseMobileTagFilter: () => void
}

const MobileFilterContainer: React.FC<MobileFilterContainerProps> = ({
  handleCloseMobileTagFilter,
  mobileTagFilterOpen,
  ...props
}) => {
  const isDesktop = useMediaQuery<Theme>(theme => theme.breakpoints.up("md"), {
    defaultMatches: false,
  })

  const handleClose = async () => {
    handleCloseMobileTagFilter()
  }

  return (
    <>
      <Dialog
        maxWidth="md"
        fullWidth={true}
        fullScreen={!isDesktop}
        open={mobileTagFilterOpen}
        onClose={handleClose}
      >
        <Box display="flex" width="100%" height={56}>
          <IconButton onClick={handleClose} aria-label="go back">
            <ArrowBackIcon />
          </IconButton>
        </Box>
        <Box paddingBottom={2}>
          <FilterContent {...props}></FilterContent>
        </Box>
        <Box
          display="flex"
          alignContent="center"
          justifyContent="center"
          position="sticky"
          bottom={8}
          width="100%"
          height={56}
        >
          <Button
            style={{ width: "70%", height: 42 }}
            variant="contained"
            color="primary"
            onClick={handleClose}
          >
            Show Results
          </Button>
        </Box>
      </Dialog>
    </>
  )
}

const drawerWidth = 280
const useStyles = makeStyles(theme => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  toolbar: {
    marginTop: theme.spacing(2),
    minHeight: 65,
  },
}))

interface DesktopFilterContainerProps extends FilterContentProps {}
const DesktopFilterContainer: React.FC<DesktopFilterContainerProps> = props => {
  const classes = useStyles()

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      className={classes.drawer}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <div className={classes.toolbar} />
      <Divider />
      <FilterContent {...props} />
    </Drawer>
  )
}

export const staticFragment = graphql`
  fragment QueryHistogramResultFragment on Hasura_query_histogram_results {
    type
    value
    value_count
  }
`

export const queryHistogramResultFragment = gql`
  fragment QueryHistogramResultFragment on query_histogram_results {
    type
    value
    value_count
  }
`
